import React from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Paper, 
  Tooltip,
  Chip
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  CheckCircle as MarkReadIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Notifications as DefaultIcon,
  Home as PropertyIcon,
  Person as ClientIcon,
  AttachMoney as PaymentIcon,
  Description as DocumentIcon,
  Event as EventIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { 
  Notification, 
  NotificationType, 
  NotificationPriority 
} from '../types';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const getIcon = (type: NotificationType) => {
  switch (type) {
    case NotificationType.PROPERTY_MATCH:
      return <PropertyIcon color="primary" />;
    case NotificationType.CLIENT_UPDATE:
      return <ClientIcon color="info" />;
    case NotificationType.SYSTEM_ALERT:
      return <ErrorIcon color="error" />;
    case NotificationType.REMINDER:
      return <WarningIcon color="warning" />;
    case NotificationType.PAYMENT:
      return <PaymentIcon color="success" />;
    case NotificationType.DOCUMENT:
      return <DocumentIcon color="action" />;
    case NotificationType.APPOINTMENT:
      return <EventIcon color="info" />;
    case NotificationType.MESSAGE:
      return <MessageIcon color="primary" />;
    default:
      return <DefaultIcon color="action" />;
  }
};

const getPriorityColor = (priority: NotificationPriority) => {
  switch (priority) {
    case NotificationPriority.URGENT:
      return 'error';
    case NotificationPriority.HIGH:
      return 'warning';
    case NotificationPriority.MEDIUM:
      return 'info';
    case NotificationPriority.LOW:
      return 'default';
    default:
      return 'default';
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete
}) => {
  const isRead = notification.read;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        bgcolor: isRead ? 'background.paper' : 'action.hover',
        borderLeft: 4,
        borderColor: isRead ? 'transparent' : 'primary.main',
        transition: 'background-color 0.3s',
        '&:hover': {
          bgcolor: 'action.selected'
        }
      }}
    >
      <Box sx={{ mr: 2, mt: 0.5 }}>
        {getIcon(notification.type)}
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
          <Typography variant="subtitle1" component="span" sx={{ fontWeight: isRead ? 'normal' : 'bold' }}>
            {notification.title}
          </Typography>
          {notification.priority !== NotificationPriority.LOW && (
            <Chip 
              label={notification.priority} 
              size="small" 
              color={getPriorityColor(notification.priority) as any} 
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {notification.message}
        </Typography>

        <Typography variant="caption" color="text.disabled">
          {notification.createdAt ? formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true }) : 'Just now'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {!isRead && (
          <Tooltip title="Mark as read">
            <IconButton onClick={() => onMarkAsRead(notification.id)} size="small" color="primary">
              <MarkReadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton onClick={() => onDelete(notification.id)} size="small" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};
